// Shared constants, formatting helpers, and small presentational
// components for the BSS dashboard. Ported from the design handoff
// and adapted to this repo (React 17, scoped `bss-` classes).

import { useEffect, useMemo, useRef } from "react";
import Icon from "./icons";
import sanitizeHtml from "./sanitizeHtml";

export const STATUS = {
  QUEUED: { label: "Queued", badge: "gray" },
  PLANNING: { label: "Planning", badge: "lilac" },
  IMPLEMENTING: { label: "Implementing", badge: "blue" },
  TESTING: { label: "Testing", badge: "mint" },
  REVIEWING: { label: "Reviewing", badge: "peach" },
  FIXING: { label: "Fixing", badge: "amber" },
  PUSHING: { label: "Pushing", badge: "pink" },
  AWAITING_APPROVAL: { label: "Awaiting approval", badge: "amber" },
  COMPLETED: { label: "Completed", badge: "green" },
  FAILED: { label: "Failed", badge: "red" }
};

// AWAITING_APPROVAL is a stable, operator-gated state: the run has paused
// pending an explicit approval and nothing changes until the operator acts,
// so for polling purposes it is treated like a settled state (no live
// spinner, polling stops) — distinct from the truly-terminal COMPLETED /
// FAILED which can never transition again.
const TERMINAL = ["COMPLETED", "FAILED"];
const SETTLED = ["COMPLETED", "FAILED", "AWAITING_APPROVAL"];

export const isLive = (status) =>
  status && status !== "QUEUED" && !SETTLED.includes(status);

export const isActive = (status) => status && !SETTLED.includes(status);

export const isTerminal = (status) => TERMINAL.includes(status);

export const isAwaitingApproval = (status) =>
  status === "AWAITING_APPROVAL";

// TicketGroup status (multi-repo fan-out). PLANNING (architect running) and
// FANNED_OUT (per-repo runs in flight) are non-terminal; COMPLETED / PARTIAL
// / FAILED are terminal so group polling stops once it settles. Unknown /
// absent status is treated as still-active so a not-yet-landed SS shape does
// not prematurely stop the poll.
export const GROUP_STATUS = {
  PLANNING: { label: "Planning", badge: "lilac" },
  FANNED_OUT: { label: "Fanned out", badge: "blue" },
  COMPLETED: { label: "Completed", badge: "green" },
  PARTIAL: { label: "Partial", badge: "amber" },
  FAILED: { label: "Failed", badge: "red" }
};

const GROUP_TERMINAL = ["COMPLETED", "PARTIAL", "FAILED"];

export const isGroupTerminal = (status) =>
  GROUP_TERMINAL.includes(status);

export const isGroupActive = (status) =>
  !GROUP_TERMINAL.includes(status);

export const GroupStatusBadge = ({ status, pulsing }) => {
  const meta = GROUP_STATUS[status] || { label: status || "—", badge: "gray" };
  return (
    <span
      className={`bss-badge bss-badge--${meta.badge}${
        pulsing ? " bss-badge--pulsing" : ""
      }`}
    >
      {pulsing && <span className="bss-dot" />}
      {meta.label}
    </span>
  );
};

export const PHASE_COLOR = {
  Planner: "var(--bss-phase-planning)",
  Implementer: "var(--bss-phase-implementing)",
  Tester: "var(--bss-phase-testing)",
  Reviewer: "var(--bss-phase-reviewing)",
  Fixer: "var(--bss-phase-fixing)",
  Pusher: "var(--bss-phase-pushing)",
  // Per-toolType display re-labels (see SS `TIMELINE_VIEWS`). These re-use
  // an existing color token so the timeline strip stays visually coherent.
  "Comment Resolver": "var(--bss-phase-implementing)", // address-comments
  "Check Fixer": "var(--bss-phase-fixing)",            // address-checks
  Refresher: "var(--bss-phase-fixing)",                // refresh / rebase
  Analyzer: "var(--bss-phase-reviewing)",              // impact-analysis (analysis surface)
  Saved: "var(--bss-phase-pushing)"                    // impact-analysis (JSON persisted to dashboard)
};

export const PHASE_ICON = {
  Planner: Icon.Sparkles,
  Implementer: Icon.Code,
  Tester: Icon.TestTube,
  Reviewer: Icon.Eye,
  Fixer: Icon.Wrench,
  Pusher: Icon.Rocket,
  // See PHASE_COLOR above — same per-toolType re-labels.
  "Comment Resolver": Icon.MessageSquare,
  "Check Fixer": Icon.AlertTriangle,
  Refresher: Icon.RotateCw,
  Analyzer: Icon.BarChart,
  Saved: Icon.Check
};

const AVATAR_PALETTE = [
  "var(--bss-avatar-pink)",
  "var(--bss-avatar-mint)",
  "var(--bss-avatar-peach)",
  "var(--bss-avatar-lilac)"
];

// CSS row class for a run/ticket-latest status.
export function rowStatusClass(status, live) {
  if (status === "FAILED") return "is-failed";
  if (live) return "is-running";
  return "";
}

// Hero variant class for the run detail header.
export function heroStatusClass(status) {
  if (status === "FAILED") return "bss-detail-hero is-failed";
  if (status === "COMPLETED") return "bss-detail-hero is-completed";
  return "bss-detail-hero is-running";
}

// Cost cell class based on budget consumption.
export function costClass(cost, budget) {
  if (cost == null || !budget) return "bss-cost";
  const pct = cost / budget;
  if (pct >= 1) return "bss-cost bss-cost-over";
  if (pct >= 0.8) return "bss-cost bss-cost-warn";
  return "bss-cost";
}

const PHASE_STATUS_CLASS = {
  done: "is-done",
  running: "is-running",
  failed: "is-failed"
};
export const phaseStatusClass = (status) =>
  PHASE_STATUS_CLASS[status] || "is-pending";

const PHASE_STRIP_COLOR = {
  done: "var(--bss-success)",
  running: "var(--bss-boom-pink)",
  failed: "var(--bss-danger)"
};
export const phaseStripColor = (status) =>
  PHASE_STRIP_COLOR[status] || "var(--bss-border-strong)";

export const PhaseGlyph = ({ status }) => {
  if (status === "done") return <Icon.Check size={10} />;
  if (status === "failed") return <Icon.X size={10} />;
  if (status === "running") return <Spinner size={8} />;
  return (
    <span
      style={{
        width: 6,
        height: 6,
        borderRadius: 999,
        background: "var(--bss-fg-subtle)"
      }}
    />
  );
};

// Outcome color for deploy / phase dots.
export function outcomeColor(status, {
  successKeys = ["success", "COMPLETED", "done"],
  failKeys = ["failed", "FAILED"]
} = {}) {
  if (successKeys.includes(status)) return "var(--bss-success)";
  if (failKeys.includes(status)) return "var(--bss-danger)";
  return "var(--bss-boom-pink)";
}

export function formatDuration(seconds) {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const m = String(Math.floor(total / 60)).padStart(2, "0");
  const s = String(total % 60).padStart(2, "0");
  return `${m}:${s}`;
}

export function formatMoney(value) {
  return `$${(Number(value) || 0).toFixed(2)}`;
}

export function relativeTime(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const diff = Date.now() - then;
  if (diff < 0) return "just now";
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}

function userInitials(name) {
  if (!name) return "?";
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function avatarColor(name) {
  const key = name || "";
  let hash = 0;
  for (let i = 0; i < key.length; i++)
    hash = (hash * 31 + key.charCodeAt(i)) | 0;
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
}

export const Avatar = ({ name, size = "md" }) => {
  const cls = size === "sm" ? "bss-avatar bss-avatar--sm" : "bss-avatar";
  const safe = name || "Unknown";
  return (
    <span className={cls} style={{ background: avatarColor(safe) }} title={safe}>
      {userInitials(safe)}
    </span>
  );
};

export const StatusBadge = ({ status, pulsing }) => {
  const def = STATUS[status];
  if (!def) return null;
  const cls = `bss-badge bss-badge--${def.badge} ${
    pulsing ? "bss-badge--pulsing" : ""
  }`.trim();
  return (
    <span className={cls}>
      <span className="bss-dot" />
      {def.label}
    </span>
  );
};

export const Kbd = ({ children }) => (
  <span className="bss-kbd">{children}</span>
);

export const Spinner = ({ size = 12, style }) => (
  <span
    className="bss-spinner"
    style={{ width: size, height: size, borderWidth: 1.5, ...style }}
  />
);

// Manual refresh control for the dashboard pages. The dashboard no longer
// polls (see useBssPoll) — this is how a user pulls fresh data on
// demand. Shows a spinner + disables itself while a fetch is in flight so a
// double-click cannot fire overlapping requests.
export const RefreshButton = ({ onClick, loading = false, label = "Refresh" }) => (
  <button
    type="button"
    className="bss-btn bss-btn--ghost"
    onClick={onClick}
    disabled={loading}
    title="Refresh data"
  >
    {loading ? <Spinner size={14} /> : <Icon.RotateCw size={14} />} {label}
  </button>
);

export const Sparkline = ({
  data,
  color = "var(--bss-boom-pink)",
  width = 76,
  height = 24
}) => {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const stepX = width / (data.length - 1);
  const pts = data.map((v, i) => {
    const x = i * stepX;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const last = data[data.length - 1];
  return (
    <svg
      width={width}
      height={height}
      className="bss-stat-spark"
      viewBox={`0 0 ${width} ${height}`}
    >
      <path
        d={`M${pts.join(" L")}`}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx={(data.length - 1) * stepX}
        cy={height - ((last - min) / range) * (height - 4) - 2}
        r="2"
        fill={color}
      />
    </svg>
  );
};

export const Stat = ({
  label,
  value,
  deltaLabel,
  deltaPositive,
  hint,
  sparkData,
  sparkColor
}) => (
  <div className="bss-stat">
    <div className="bss-stat-label">{label}</div>
    <div className="bss-stat-value">{value}</div>
    {(deltaLabel || hint) && (
      <div className="bss-stat-meta">
        {deltaLabel && (
          <span className={deltaPositive ? "bss-up" : "bss-down"}>
            {deltaPositive ? "↑" : "↓"} {deltaLabel}
          </span>
        )}
        {hint && <span>{hint}</span>}
      </div>
    )}
    {sparkData && <Sparkline data={sparkData} color={sparkColor} />}
  </div>
);

function meterClass(pct) {
  if (pct >= 100) return "bss-meter-fill is-over";
  if (pct >= 80) return "bss-meter-fill is-warn";
  return "bss-meter-fill";
}

export const ProgressBar = ({ value, max }) => {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  const cls = meterClass(pct);
  return (
    <div className="bss-meter">
      <div className={cls} style={{ width: `${pct}%` }} />
    </div>
  );
};

export const Modal = ({ open, onClose, title, children, footer, width, height }) => {
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === "Escape") onClose && onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Lock the page behind the backdrop so scrolling/trackpad over the modal
  // doesn't bleed into the page. Ref-counted so nested modals don't restore
  // early.
  useEffect(() => {
    if (!open) return undefined;
    const b = document.body;
    const n = (Number(b.dataset.jaModalLock) || 0) + 1;
    b.dataset.jaModalLock = String(n);
    if (n === 1) {
      b.dataset.jaModalPrevOverflow = b.style.overflow || "";
      b.style.overflow = "hidden";
    }
    return () => {
      const left = (Number(b.dataset.jaModalLock) || 1) - 1;
      if (left <= 0) {
        delete b.dataset.jaModalLock;
        b.style.overflow = b.dataset.jaModalPrevOverflow || "";
        delete b.dataset.jaModalPrevOverflow;
      } else {
        b.dataset.jaModalLock = String(left);
      }
    };
  }, [open]);

  if (!open) return null;
  return (
    <div
      className="bss-modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && onClose) onClose();
      }}
    >
      <div
        className="bss-modal"
        style={
          width || height
            ? { ...(width ? { width } : {}), ...(height ? { height } : {}) }
            : undefined
        }
      >
        <div className="bss-modal-hd">
          <h2>{title}</h2>
          <button
            type="button"
            className="bss-icon-btn"
            style={{ marginLeft: "auto" }}
            onClick={onClose}
            aria-label="Close"
          >
            <Icon.X size={16} />
          </button>
        </div>
        <div className="bss-modal-bd">{children}</div>
        {footer && <div className="bss-modal-ft">{footer}</div>}
      </div>
    </div>
  );
};

export const RadioCard = ({ active, onClick, title, desc, icon }) => (
  <button
    type="button"
    className={`bss-radio-card ${active ? "is-active" : ""}`.trim()}
    onClick={onClick}
  >
    <div className="bss-radio-card-title">
      <span className="bss-radio-dot" />
      {icon}
      <span>{title}</span>
    </div>
    <div className="bss-radio-card-desc">{desc}</div>
  </button>
);

export const Toggle = ({ on, onChange, disabled }) => (
  <button
    type="button"
    className={`bss-toggle ${on ? "is-on" : ""}`.trim()}
    onClick={() => !disabled && onChange && onChange(!on)}
    aria-pressed={on}
    disabled={disabled}
  />
);

function inlineMd(s) {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\*([^*]+)\*/g, "<em>$1</em>");
}

const HEADING = [
  ["#### ", "h4"],
  ["### ", "h3"],
  ["## ", "h3"]
];

function headingFor(line) {
  for (const [prefix, tag] of HEADING)
    if (line.startsWith(prefix))
      return `<${tag}>${inlineMd(line.slice(prefix.length))}</${tag}>`;
  return null;
}

// Collect a contiguous block of lines that satisfy `match`, returning the
// rendered HTML and the index just past the block.
function collectBlock(lines, start, match, render) {
  const block = [];
  let i = start;
  while (i < lines.length && match(lines[i])) {
    block.push(lines[i]);
    i++;
  }
  return [render(block), i];
}

const isOrdered = (s) => /^\d+\.\s/.test(s);
const isBullet = (s) => s.startsWith("- ");
const isQuote = (s) => s.startsWith("> ");

function isParaLine(s) {
  return (
    s.trim() !== "" &&
    !isBullet(s) &&
    !s.startsWith("#") &&
    !isOrdered(s)
  );
}

export function renderMd(src) {
  if (!src) return "";
  const lines = String(src).split("\n");
  const out = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const heading = headingFor(line);
    if (heading) {
      out.push(heading);
      i++;
    } else if (isQuote(line)) {
      let html;
      [html, i] = collectBlock(
        lines,
        i,
        isQuote,
        (b) => `<blockquote>${inlineMd(b.map((x) => x.slice(2)).join(" "))}</blockquote>`
      );
      out.push(html);
    } else if (isOrdered(line)) {
      let html;
      [html, i] = collectBlock(
        lines,
        i,
        isOrdered,
        (b) =>
          `<ol>${b
            .map((x) => `<li>${inlineMd(x.replace(/^\d+\.\s/, ""))}</li>`)
            .join("")}</ol>`
      );
      out.push(html);
    } else if (isBullet(line)) {
      let html;
      [html, i] = collectBlock(
        lines,
        i,
        isBullet,
        (b) =>
          `<ul>${b
            .map((x) => `<li>${inlineMd(x.slice(2))}</li>`)
            .join("")}</ul>`
      );
      out.push(html);
    } else if (line.trim() === "") {
      i++;
    } else {
      let html;
      const before = i;
      [html, i] = collectBlock(
        lines,
        i,
        isParaLine,
        (b) => `<p>${inlineMd(b.join(" "))}</p>`
      );
      if (i === before) {
        // Defensive guard: no branch matched and collectBlock could not
        // advance (e.g. a `# single hash`, ``` fence, table or <details>
        // line that is non-empty but not a recognized paragraph). Emit the
        // line as an escaped paragraph and force progress so the outer
        // loop can never infinite-loop on any input.
        out.push(`<p>${inlineMd(line)}</p>`);
        i++;
      } else {
        out.push(html);
      }
    }
  }
  return out.join("");
}

export const Markdown = ({ src }) => (
  // Content is escaped in inlineMd before limited markup is reintroduced.
  <div className="bss-md" dangerouslySetInnerHTML={{ __html: renderMd(src) }} />
);

// Renders agent HTML. The shipping-service runs agent output through a
// server-side allowlist sanitizer (scripts / on* handlers / javascript:
// URLs stripped) — that is the primary trust boundary. As defense in depth
// we ALSO run an independent client-side allowlist sanitizer here before
// injection, so a server-side bypass / regression cannot result in XSS in
// the browser. See ./sanitizeHtml.
export const Html = ({ src }) => (
  <div
    className="bss-md"
    dangerouslySetInnerHTML={{ __html: sanitizeHtml(src) }}
  />
);

export const LogView = ({ rows, live }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (live && ref.current) ref.current.scrollTop = ref.current.scrollHeight;
  }, [rows, live]);
  if (!rows || rows.length === 0)
    return (
      <div className="bss-log" style={{ color: "var(--bss-fg-subtle)" }}>
        No log output.
      </div>
    );
  return (
    <div className="bss-log" ref={ref}>
      {rows.map((row, i) => (
        <div key={i} className={`bss-log-row is-${row.level || "info"}`}>
          <span className="bss-ts">
            {row.ts ? new Date(row.ts).toLocaleTimeString() : ""}
          </span>
          <span className="bss-msg">{row.message}</span>
        </div>
      ))}
    </div>
  );
};

export const ErrorState = ({ message, onRetry }) => (
  <div className="bss-state">
    <Icon.AlertTriangle
      size={28}
      style={{ color: "var(--bss-danger)", marginBottom: 10 }}
    />
    <div style={{ color: "var(--bss-fg-strong)", fontWeight: 500 }}>
      {message || "Something went wrong."}
    </div>
    {onRetry && (
      <button
        type="button"
        className="bss-btn bss-btn--ghost bss-btn--sm"
        style={{ marginTop: 14 }}
        onClick={onRetry}
      >
        <Icon.RotateCw size={12} /> Retry
      </button>
    )}
  </div>
);

export const LoadingState = ({ label = "Loading…" }) => (
  <div className="bss-state">
    <Spinner size={20} style={{ color: "var(--bss-boom-pink)" }} />
    <div style={{ marginTop: 12 }}>{label}</div>
  </div>
);

export const EmptyState = ({ title, hint }) => (
  <div className="bss-empty">
    <Icon.Inbox
      size={32}
      style={{ color: "var(--bss-fg-subtle)", marginBottom: 12 }}
    />
    <div style={{ fontWeight: 500, color: "var(--bss-fg)" }}>
      {title || "Nothing here yet."}
    </div>
    {hint && (
      <div style={{ fontSize: 12.5, marginTop: 4 }}>{hint}</div>
    )}
  </div>
);

// Extract the API error message regardless of axios/blob shape.
export function apiErrorMessage(err, fallback = "Request failed") {
  const data = err && err.response && err.response.data;
  if (data && typeof data === "object") {
    // Tolerate both shapes the upstream APIs use:
    //   {message: "..."}  or  {error: "..."}         ← string
    //   {error: {message: "...", status: 401}}        ← nested object (api-2 auth errors)
    // Without unwrapping the nested case we'd return an object and crash
    // React with "Objects are not valid as a React child".
    if (typeof data.message === "string") return data.message;
    if (typeof data.error === "string") return data.error;
    if (data.error && typeof data.error.message === "string") return data.error.message;
    return fallback;
  }
  return (err && err.message) || fallback;
}

export function useDocumentTitle(title) {
  useMemo(() => {
    if (title) document.title = title;
  }, [title]);
}
